import { useThemeStore } from '@/stores'
import { scrollToSelector } from '@/utils'
import { BUNDLE_COLLECTION_ID, COFFEE_BUNDLE_COLLECTION_ID, COFFEE_BUNDLE_SKU } from '@/services/constants'
import BundleSummaryHalfSheet from '@/components/bundle/bundle-summary-half-sheet.vue'

import type {
  BundlePlan,
  BundleProduct,
  BundleProductVariant,
  BundleProductSelection,
  BundleSize,
  BundlePricing,
  BundleType
} from '@/types/bundle'

import type {
  BundleAppProxy,
  BundleSelectionAppProxy
} from '@rechargeapps/storefront-client'

export const FIRST_TIME_PERCENT = 25

export const PRICING: Record<BundleSize, BundlePricing> = {
  '3': {
    minPrice: '$27',
    minCents: 2700,
    savings: '$4',
    firstSavings: '$9',
    monthlyPrice: '$32',
    onetimePrice: '$36',
    onetimeCents: 3600
  },
  '6': {
    minPrice: '$54',
    minCents: 5400,
    savings: '$12',
    firstSavings: '$18',
    monthlyPrice: '$60',
    onetimePrice: '$72',
    onetimeCents: 7200
  },
  '16': {
    minPrice: '$45',
    minCents: 4500,
    savings: '$2',
    firstSavings: '$15',
    monthlyPrice: '$58',
    onetimePrice: '$60',
    onetimeCents: 6000
  },
  '24': {
    minPrice: '$63',
    minCents: 6300,
    savings: '$6',
    firstSavings: '$21',
    monthlyPrice: '$78',
    onetimePrice: '$84',
    onetimeCents: 8400
  },
  '32': {
    minPrice: '$79',
    minCents: 7900,
    savings: '$7',
    firstSavings: '$27',
    monthlyPrice: '$99',
    onetimePrice: '$106',
    onetimeCents: 10600
  }
} as const

export const createBundleSelections = (selection: BundleProductSelection[], bundleType: BundleType) => {
  return selection
    .sort((a, b) => (a.product.title.localeCompare(b.product.title)))
    .sort((a, b) => (b.quantity - a.quantity))
    .map(({ key, quantity }) => {
      const [ externalProductId, externalVariantId ] = key.split('/')
      return {
        collectionId: bundleType === 'coffee' ? COFFEE_BUNDLE_COLLECTION_ID : BUNDLE_COLLECTION_ID,
        externalProductId,
        externalVariantId,
        quantity
      } as BundleSelectionAppProxy
    })
}

export const createBundleSelection = (product: BundleProduct, variant: BundleProductVariant, selection: BundleProductSelection[]): BundleAppProxy => {
  const bundleType = COFFEE_BUNDLE_SKU.test(variant.sku) ? 'coffee' : 'oatmeal'
  return {
    externalProductId: String(product.nid),
    externalVariantId: String(variant.nid),
    selections: createBundleSelections(selection, bundleType)
  }
}

export const sortBundleProductSelections = (selection: BundleProductSelection[]) => {
  return [ ...selection ]
    .sort((a, b) => (a.product.title.localeCompare(b.product.title)))
    .sort((a, b) => (b.quantity - a.quantity))
}

export const getBundleSellingPlanId = (product: BundleProduct, plan: BundlePlan) => {
  if (plan === 'onetime') {
    return
  }

  const sellingPlan = product.sellingPlans?.find(i => i.recurringDeliveries)
  return sellingPlan?.nid
}

export const bundleJSONContents = (selection: BundleProductSelection[]) => {
  return selection.map(item => {
    const { quantity, product: { title } } = item
    return { quantity, title }
  })
}

export const showBundleSummary = () => {
  const { sheet } = useThemeStore()
  const $el: HTMLElement | null = document.querySelector('.sidebar .bundle-summary')
  if ($el?.offsetParent) {
    scrollToSelector('.product-scroll-container', 17)
    highlightBundleSummary()
  } else if (!sheet.visible) {
    sheet.open(BundleSummaryHalfSheet, { hideCloseButton: true, noPadding: true })
  }
}

const highlightBundleSummary = () => {
  const summary = document.querySelector('.bundle-summary')
  if (summary) {
    summary.classList.add('highlight')
    setTimeout(() => {
      summary.classList.remove('highlight')
    }, 1000)
  }
}
